<template>
  <label
    class="text-sm"
    :class="[
      $style.container,
      {
        [$style.disabled]: disabled,
        [$style.noTransition]: noTransition,
        [$style.multiline]: help || $slots.secondary,
        [$style.alignTop]: multiline,
      },
    ]"
    v-on="getListeners"
  >
    <span :class="[$style.checkbox, { [$style.checked]: checked }]">
      <svg v-if="checked || preserveCheckIcon" width="16" height="16" fill="#fff">
        <path
          d="M10.9 4.8a1 1 0 011.4 0l.1.1c.3.4.3 1 0 1.4L7.8 11a1 1 0 01-1.5 0L3.7 8.2a1 1 0 111.5-1.4l1.9 2 3.8-4z"
        />
      </svg>
      <svg
        v-if="indeterminate"
        data-testid="checkbox-indeterminate"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <rect width="16" height="16" rx="3" class="fill-form-selected-background" />
        <rect x="4" y="7" width="8" height="2" rx="1" fill="#fff" />
      </svg>
      <input
        type="checkbox"
        :name="name"
        :required="required"
        :disabled="disabled"
        :checked="checked"
        :style="inputStyle"
        :class="[$style.input, error && $style.error]"
        :tabindex="tabindex"
        @change="handleChange"
      />
    </span>
    <span :class="[light ? $style.labelLight : $style.label, { [$style.multilineLabel]: multiline }]">
      <slot>{{ label }}</slot>
    </span>
    <span v-if="help || $slots.secondary" class="text-gray-60 text-xs" :class="$style.help">
      <slot name="secondary">{{ help }}</slot>
    </span>
  </label>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from 'vue'

const props = withDefaults(
  defineProps<{
    label?: string
    help?: string
    name?: string
    required?: boolean
    disabled?: boolean
    multiline?: boolean
    checked?: boolean
    indeterminate?: boolean
    noTransition?: boolean
    light?: boolean
    preserveCheckIcon?: boolean
    color?: string
    tabindex?: string
    error?: boolean
  }>(),
  {
    label: '',
    help: '',
    name: '',
    checked: false,
    error: false,
    color: '',
    tabindex: '',
  },
)

const emit = defineEmits<{
  (e: 'change', value: boolean): void
}>()

const listeners = useAttrs()

const inputStyle = {
  '--background-color': props.color || 'var(--form-selected-background)',
}

const getListeners = computed(() => {
  const { change, class: clazz, ...otherListeners } = listeners
  return otherListeners
})

const handleChange = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) return

  emit('change', event.target.checked)
}
</script>

<style lang="scss" module>
.container {
  position: relative;
  display: grid;
  grid-template-areas: 'toggle label';
  grid-template-columns: 16px 1fr;
  grid-gap: 2px 8px;
  align-items: center;
  user-select: none;
  flex-wrap: wrap;
  cursor: pointer;

  &.disabled {
    color: var(--gray-50);
    cursor: not-allowed;
  }

  &.noTransition .input {
    transition: none;
  }

  .checkbox {
    & > svg {
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
    }

    &.checked .input {
      border-color: var(--background-color);
      background-color: var(--background-color);
    }
  }

  .input {
    appearance: none;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: solid 1px var(--gray-40);
    background-color: var(--white);
    transition: all 0.2s;
    cursor: pointer;

    &.error {
      background-color: var(--red-10);
      border-color: var(--red-40);
    }

    &:disabled {
      cursor: not-allowed;
      background-color: var(--gray-30);
    }

    &:disabled:checked {
      border-color: var(--form-disabled-background);
      background-color: var(--form-disabled-background);
    }

    &:hover,
    &:active {
      border-color: var(--background-color);
      box-shadow: 0px 0px 4px rgba(71, 93, 229, 0.4);
    }
  }
}

.alignTop {
  align-items: flex-start;

  .checkbox {
    margin-top: 2px; /* aligns better with label since font has small offset from top */
  }
}

.multiline {
  grid-template-areas:
    'toggle label'
    'toggle help';
}

.label {
  grid-area: label;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gray-80);
  font-weight: theme('fontWeight.medium');
  margin-left: -2px;
  padding-left: 2px;
  user-select: text;
}

.labelLight {
  composes: label;
  color: var(--white);
}

.help {
  grid-area: help;
  display: block;
}

.checkbox {
  position: relative;
  display: flex;
  align-items: center;
}

.multilineLabel {
  white-space: unset;
}
</style>
